import React, { useRef, useImperativeHandle, forwardRef } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { StaffState } from "types/staff-edit.types";
import { Field, FieldRef } from "../Field/Field";
import { CustomerRules, phoneNumber } from "utils/formRules";

interface ContactEmergencySectionProps {
  contact: StaffState["contactInfo"];
  emergency: StaffState["emergencyInfo"];
  onUpdate: (path: string, value: string) => void;
}

// Định nghĩa interface cho Ref để trang cha sử dụng
export interface ContactEmergencySectionRef {
  validateAll: () => Promise<boolean>;
}

export const ContactEmergencySection = forwardRef<
  ContactEmergencySectionRef,
  ContactEmergencySectionProps
>(({ contact, emergency, onUpdate }, ref) => {
  // Tạo Refs cho tất cả các Field cần validate
  const phoneNumberRef = useRef<FieldRef>(null);
  const nationalityPhoneRef = useRef<FieldRef>(null);
  const homePhoneRef = useRef<FieldRef>(null);
  const relationshipRef = useRef<FieldRef>(null);
  const emailRef = useRef<FieldRef>(null);
  const currentAddressRef = useRef<FieldRef>(null);
  const permanentAddressRef = useRef<FieldRef>(null);

  // Công khai hàm validateAll
  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const results = await Promise.all([
        phoneNumberRef.current?.validate(),
        nationalityPhoneRef.current?.validate(),
        homePhoneRef.current?.validate(),
        relationshipRef.current?.validate(),
        emailRef.current?.validate(),
        currentAddressRef.current?.validate(),
        permanentAddressRef.current?.validate(),
      ]);
      return results.every((r) => r === true);
    },
  }));

  return (
    <FourColumnRowSection
      leftTitle="Thông tin liên lạc"
      rightTitle="Thông tin khẩn"
      rows={[
        {
          leftLabel: "Điện thoại di động",
          leftValue: (
            <Field
              ref={phoneNumberRef}
              path="contactInfo.phoneNumber"
              value={contact.phoneNumber}
              onUpdate={onUpdate}
              placeholder="Nhập SĐT di động"
              rules={phoneNumber}
            />
          ),
          rightLabel: "SĐT theo quốc tịch",
          rightValue: (
            <Field
              ref={nationalityPhoneRef}
              path="emergencyInfo.nationalityPhone"
              value={emergency.nationalityPhone}
              onUpdate={onUpdate}
              rules={CustomerRules}
            />
          ),
        },
        {
          leftLabel: "Điện thoại nhà",
          leftValue: (
            <Field
              ref={homePhoneRef}
              path="contactInfo.homePhone"
              value={contact.homePhone}
              onUpdate={onUpdate}
              placeholder="Nhập SĐT nhà"
              rules={phoneNumber}
            />
          ),
          rightLabel: "Mối quan hệ",
          rightValue: (
            <Field
              ref={relationshipRef}
              path="emergencyInfo.relationship"
              value={emergency.relationship}
              onUpdate={onUpdate}
              options={["Bố", "Mẹ", "Vợ", "Chồng", "Anh/Em"]}
              placeholder="Chọn mối quan hệ"
              rules={CustomerRules}
            />
          ),
        },
        {
          leftLabel: "Địa chỉ hiện tại",
          leftValue: (
            <Field
              ref={currentAddressRef}
              path="contactInfo.currentAddress"
              value={contact.currentAddress}
              onUpdate={onUpdate}
              placeholder="Số nhà, tên đường..."
              rules={CustomerRules}
            />
          ),
          rightLabel: "Địa chỉ thường trú",
          rightValue: (
            <Field
              ref={permanentAddressRef}
              path="contactInfo.permanentAddress"
              value={contact.permanentAddress}
              onUpdate={onUpdate}
              placeholder="Địa chỉ theo hộ khẩu"
              rules={CustomerRules}
            />
          ),
        },
      ]}
    />
  );
});

ContactEmergencySection.displayName = "ContactEmergencySection";
